import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Users, Wallet } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SimpleBarChart } from './SimpleBarChart';
import { SimpleDonutChart } from './SimpleDonutChart';
import { MonthlyIndicator } from '../services/dashboard-rh.service';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ComposedChart,
  Line,
  Legend,
} from 'recharts';

interface DashboardChartsProps {
  monthlyIndicators: MonthlyIndicator[];
  lastMonth: MonthlyIndicator;
  formatCurrency: (value: number) => string;
  formatPercent: (value: number) => string;
}

const MONTHS = [
  'Jan',
  'Fév',
  'Mar',
  'Avr',
  'Mai',
  'Jun',
  'Jul',
  'Aoû',
  'Sep',
  'Oct',
  'Nov',
  'Déc',
];

const formatMonthLabel = (value: unknown): string => {
  if (typeof value !== 'object' || value === null) return '';

  const { mois, annee } = value as Partial<MonthlyIndicator>;
  if (typeof mois !== 'number' || typeof annee !== 'number') return '';

  return `${MONTHS[mois - 1] ?? ''} ${annee}`.trim();
};

export function DashboardCharts({
  monthlyIndicators,
  lastMonth,
  formatCurrency,
}: DashboardChartsProps) {
  const { t } = useTranslation();

  // Prepare data for charts
  const financialChartData = monthlyIndicators.map(m => ({
    label: `${MONTHS[m.mois - 1]} ${m.annee.toString().slice(-2)}`,
    value: m.masseSalarialeBrute || 0,
  }));

  const costChartData = monthlyIndicators.map(m => ({
    label: `${MONTHS[m.mois - 1]} ${m.annee.toString().slice(-2)}`,
    value: m.coutGlobalEmployeur || 0,
  }));

  return (
    <Tabs defaultValue="financial" className="w-full">
      <TabsList className="mb-6 w-full justify-start">
        <TabsTrigger value="financial" className="gap-2">
          <Wallet className="h-4 w-4" />
          {t('dashboardRh.tabs.financial', 'Vue Financière')}
        </TabsTrigger>
        <TabsTrigger value="hr" className="gap-2">
          <Users className="h-4 w-4" />
          {t('dashboardRh.tabs.hr', 'Vue RH')}
        </TabsTrigger>
      </TabsList>

      {/* Financial View */}
      <TabsContent value="financial" className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Masse salariale evolution */}
          <div className="rounded-xl border border-neutral-200 bg-white p-6">
            <h3 className="mb-4 text-lg font-semibold text-neutral-900">
              {t(
                'dashboardRh.charts.masseSalariale',
                'Évolution masse salariale brute'
              )}
            </h3>
            <SimpleBarChart
              data={financialChartData.map(d => ({ ...d, color: '#3b82f6' }))}
              formatValue={formatCurrency}
            />
          </div>

          {/* Coût global evolution */}
          <div className="rounded-xl border border-neutral-200 bg-white p-6">
            <h3 className="mb-4 text-lg font-semibold text-neutral-900">
              {t(
                'dashboardRh.charts.coutGlobal',
                'Évolution coût global employeur'
              )}
            </h3>
            <SimpleBarChart
              data={costChartData.map(d => ({ ...d, color: '#10b981' }))}
              formatValue={formatCurrency}
            />
          </div>

          {/* Charges patronales breakdown */}
          <div className="rounded-xl border border-neutral-200 bg-white p-6">
            <h3 className="mb-4 text-lg font-semibold text-neutral-900">
              {t(
                'dashboardRh.charts.chargesPatronales',
                'Répartition charges patronales'
              )}
            </h3>
            <SimpleBarChart
              data={monthlyIndicators.slice(-6).map(m => ({
                label: `${MONTHS[m.mois - 1]} ${m.annee.toString().slice(-2)}`,
                value: m.chargesPatronalesNettes || 0,
                color: '#f59e0b',
              }))}
              formatValue={formatCurrency}
            />
          </div>

          {/* Additional financial metrics */}
          <div className="rounded-xl border border-neutral-200 bg-white p-6">
            <h3 className="mb-4 text-lg font-semibold text-neutral-900">
              {t('dashboardRh.charts.metrics', 'Métriques financières')}
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600">
                  {t('dashboardRh.metrics.netAPayer', 'Net à payer')}
                </span>
                <span className="text-lg font-semibold text-neutral-900">
                  {formatCurrency(lastMonth.netAPayer || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600">
                  {t(
                    'dashboardRh.metrics.chargesSalariales',
                    'Charges salariales'
                  )}
                </span>
                <span className="text-lg font-semibold text-neutral-900">
                  {formatCurrency(lastMonth.chargesSalarialesTotales || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600">
                  {t(
                    'dashboardRh.metrics.heuresSupp',
                    'Heures supplémentaires'
                  )}
                </span>
                <span className="text-lg font-semibold text-neutral-900">
                  {formatCurrency(lastMonth.heuresSupplementairesTotal || 0)}h
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-neutral-600">
                  {t(
                    'dashboardRh.metrics.montantHeuresSupp',
                    'Montant heures supp.'
                  )}
                </span>
                <span className="text-lg font-semibold text-neutral-900">
                  {formatCurrency(lastMonth.montantHeuresSupplementaires || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </TabsContent>

      {/* HR View */}
      <TabsContent value="hr" className="space-y-6">
        <div className="grid gap-6 lg:grid-cols-1">
          {/* Combined RH Indicators Chart */}
          <div className="rounded-xl border border-neutral-200 bg-white p-6">
            <h3 className="mb-4 text-lg font-semibold text-neutral-900">
              {t('dashboardRh.charts.indicateursRH', 'Indicateurs RH')}
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <ComposedChart data={monthlyIndicators}>
                <defs>
                  <linearGradient
                    id="colorAbsenteisme"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient
                    id="colorTurnover"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey={m =>
                    `${MONTHS[m.mois - 1]} ${m.annee.toString().slice(-2)}`
                  }
                  stroke="#6b7280"
                />
                <YAxis
                  yAxisId="left"
                  stroke="#6b7280"
                  label={{
                    value: 'Taux (%)',
                    angle: -90,
                    position: 'insideLeft',
                  }}
                  domain={[0, 'auto']}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="#6b7280"
                  label={{
                    value: 'Effectif',
                    angle: 90,
                    position: 'insideRight',
                  }}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="rounded-lg border border-neutral-200 bg-white p-3 shadow-lg">
                          <p className="mb-2 text-sm font-semibold text-neutral-900">
                            {formatMonthLabel(payload[0]?.payload)}
                          </p>
                          {payload.map((entry, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2 text-sm"
                            >
                              <div
                                className="h-3 w-3 rounded-full"
                                style={{ backgroundColor: entry.color }}
                              />
                              <span className="text-neutral-600">
                                {entry.name}:
                              </span>
                              <span
                                className="font-semibold"
                                style={{ color: entry.color }}
                              >
                                {entry.name === 'Effectif'
                                  ? entry.value
                                  : `${(entry.value as number).toFixed(2)}%`}
                              </span>
                            </div>
                          ))}
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="tauxAbsenteisme"
                  stroke="#ef4444"
                  strokeWidth={3}
                  name="Absentéisme (%)"
                  dot={{ fill: '#ef4444', r: 5 }}
                  activeDot={{ r: 7 }}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="tauxTurnover"
                  stroke="#f59e0b"
                  strokeWidth={3}
                  name="Turnover (%)"
                  dot={{ fill: '#f59e0b', r: 5 }}
                  activeDot={{ r: 7 }}
                />
                <Bar
                  yAxisId="right"
                  dataKey="effectif"
                  fill="#3b82f6"
                  fillOpacity={0.6}
                  radius={[8, 8, 0, 0]}
                  name="Effectif"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-neutral-200 bg-white p-6">
            <h3 className="mb-4 text-lg font-semibold text-neutral-900">
              {t('dashboardRh.charts.egaliteHF', 'Égalité Hommes-Femmes')}
            </h3>
            <div className="grid grid-cols-1 gap-6">
              <div>
                <h4 className="mb-2 text-sm font-medium text-neutral-500">
                  Répartition effectifs
                </h4>
                <SimpleDonutChart
                  data={[
                    {
                      label: t('dashboardRh.gender.hommes', 'Hommes'),
                      value: lastMonth.nbHommes || 0,
                      color: '#3b82f6',
                    },
                    {
                      label: t('dashboardRh.gender.femmes', 'Femmes'),
                      value: lastMonth.nbFemmes || 0,
                      color: '#ec4899',
                    },
                  ]}
                  formatValue={(v: number) => v.toString()}
                />
              </div>

              <div>
                <h4 className="mb-2 text-sm font-medium text-neutral-500">
                  Comparaison rémunération moyenne
                </h4>
                <div className="h-[200px] w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={[
                        {
                          name: t('dashboardRh.gender.hommes', 'Hommes'),
                          value: lastMonth.remunerationMoyenneHommes || 0,
                          fill: '#3b82f6',
                        },
                        {
                          name: t('dashboardRh.gender.femmes', 'Femmes'),
                          value: lastMonth.remunerationMoyenneFemmes || 0,
                          fill: '#ec4899',
                        },
                      ]}
                      margin={{ top: 5, right: 30, left: 40, bottom: 5 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        horizontal={true}
                        vertical={true}
                        stroke="#e5e7eb"
                      />
                      <XAxis
                        type="number"
                        stroke="#6b7280"
                        tickFormatter={value => `${value}€`}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        stroke="#6b7280"
                        width={60}
                      />
                      <Tooltip
                        cursor={{ fill: 'transparent' }}
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="rounded-lg border border-neutral-200 bg-white p-2 shadow-sm">
                                <p className="text-sm font-medium text-neutral-900">
                                  {payload[0].payload.name}
                                </p>
                                <p className="text-sm text-neutral-600">
                                  {formatCurrency(payload[0].value as number)}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={32}>
                        <Cell fill="#3b82f6" />
                        <Cell fill="#ec4899" />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>

          {/* Movements */}
          <div className="rounded-xl border border-neutral-200 bg-white p-6">
            <h3 className="mb-4 text-lg font-semibold text-neutral-900">
              {t('dashboardRh.charts.mouvements', 'Mouvements du personnel')}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={monthlyIndicators.slice(-6)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey={m =>
                    `${MONTHS[m.mois - 1]} ${m.annee.toString().slice(-2)}`
                  }
                  stroke="#6b7280"
                />
                <YAxis stroke="#6b7280" />
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="rounded-lg border border-neutral-200 bg-white p-3 shadow-lg">
                          <p className="mb-2 text-sm font-semibold text-neutral-900">
                            {formatMonthLabel(payload[0]?.payload)}
                          </p>
                          {payload.map((entry, index) => (
                            <div
                              key={index}
                              className="flex items-center gap-2 text-sm"
                            >
                              <div
                                className="h-3 w-3 rounded-full"
                                style={{ backgroundColor: entry.color }}
                              />
                              <span className="text-neutral-600">
                                {entry.name}:
                              </span>
                              <span
                                className="font-semibold"
                                style={{ color: entry.color }}
                              >
                                {entry.value}
                              </span>
                            </div>
                          ))}
                          <div className="mt-2 border-t border-neutral-200 pt-2">
                            <div className="flex items-center gap-2 text-sm font-semibold">
                              <span className="text-neutral-600">Total:</span>
                              <span className="text-neutral-900">
                                {payload.reduce(
                                  (sum, entry) => sum + (entry.value as number),
                                  0
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar
                  dataKey="nbArrivees"
                  stackId="a"
                  fill="#10b981"
                  radius={[0, 0, 0, 0]}
                  name="Arrivées"
                />
                <Bar
                  dataKey="nbDeparts"
                  stackId="a"
                  fill="#ef4444"
                  radius={[8, 8, 0, 0]}
                  name="Départs"
                />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-red-500"></div>
                  <span className="text-neutral-600">
                    {t('dashboardRh.movements.departs', 'Départs')}
                  </span>
                </div>
                <span className="font-medium text-neutral-900">
                  {lastMonth.nbDeparts || 0}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-green-500"></div>
                  <span className="text-neutral-600">
                    {t('dashboardRh.movements.arrivees', 'Arrivées')}
                  </span>
                </div>
                <span className="font-medium text-neutral-900">
                  {lastMonth.nbArrivees || 0}
                </span>
              </div>
              <div className="flex items-center justify-between border-t border-neutral-200 pt-2 text-sm">
                <span className="font-medium text-neutral-600">
                  {t('dashboardRh.movements.effectif', 'Effectif')}
                </span>
                <span className="font-semibold text-neutral-900">
                  {lastMonth.effectif || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  );
}
